/**
 * Freezes expensive page media while the header mega-menu / mobile drawer is
 * open, so the full-viewport MenuScrim `backdrop-filter: blur(32px)` samples a
 * still frame instead of live video / WebGL / carousel motion.
 *
 * Intentionally does NOT call `lenis.stop()` / `pauseHomepageLenis()`. Lenis
 * stop adds `.lenis-stopped` → our HomepageLenis CSS sets `overflow: hidden`,
 * which removes the classic scrollbar and expands the layout viewport (~15px).
 * Closing the menu restores the scrollbar → visible width jump ("big → small").
 * The mega-menu was never scroll-locked (`MenuScrim` is pointer-events:none);
 * pausing video / WebGL / Embla is enough for the blur-cost win. Real scroll
 * locks (compare modal, etc.) keep using `pauseHomepageLenis()` and should
 * compensate the gutter if they hide overflow.
 *
 * Visual treatment of the scrim stays identical on every page — only media
 * stops moving. Ref-counted so nested callers don't resume early.
 */

type FreezeListener = (frozen: boolean) => void;

const DATA_ATTR = 'uhMenuBackdropFrozen';

let freezeCount = 0;
let pausedVideos: HTMLVideoElement[] = [];
const listeners = new Set<FreezeListener>();

export function isBackdropFrozen(): boolean {
  return freezeCount > 0;
}

export function subscribeBackdropFreeze(listener: FreezeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(frozen: boolean): void {
  if (typeof document !== 'undefined') {
    if (frozen) {
      document.documentElement.dataset[DATA_ATTR] = '1';
    } else {
      delete document.documentElement.dataset[DATA_ATTR];
    }
  }

  listeners.forEach((listener) => listener(frozen));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('uh:menu-backdrop-freeze', { detail: { frozen } }),
    );
  }
}

function pausePlayingVideos(): void {
  pausedVideos = [];
  if (typeof document === 'undefined') return;

  document.querySelectorAll('video').forEach((node) => {
    const video = node as HTMLVideoElement;
    // Scroll-scrubbed / caller-owned videos opt out via data attr — pausing
    // is fine for the freeze visual, but auto-play on resume would fight GSAP.
    if (video.dataset.uhManualVideo === '1') {
      video.pause();
      return;
    }
    if (!video.paused && !video.ended) {
      video.pause();
      pausedVideos.push(video);
    }
  });
}

function resumePausedVideos(): void {
  const toResume = pausedVideos;
  pausedVideos = [];
  toResume.forEach((video) => {
    if (!video.isConnected) return;
    void video.play().catch(() => undefined);
  });
}

/** Acquire a freeze — first caller pauses playing videos and notifies listeners. */
export function freezeBackdropForMenu(): void {
  freezeCount += 1;
  if (freezeCount !== 1) return;

  pausePlayingVideos();
  notify(true);
}

/** Release a freeze — last caller resumes the videos it paused and notifies. */
export function unfreezeBackdropForMenu(): void {
  if (freezeCount === 0) return;
  freezeCount -= 1;
  if (freezeCount !== 0) return;

  resumePausedVideos();
  notify(false);
}
